import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { partnerErrorMessage } from '../state/partnerError';
import { stagingBannerEnabled, useMocks } from '../../../shared/env';
import { RelationshipBadge } from '../components/StatusBadge';
import { Unavailable } from '../components/Unavailable';
import { invitationLink } from '../invitationLink';
import { CLIENT_WORKSPACE_SECTIONS } from '../partnerNav';
import { partnerGateway } from '../services';
import { useLoad } from '../state/useLoad';
import { useClientSession } from '../state/useClientSession';
import { usePartner } from '../state/usePartner';
import { PARTNER_ROLE_LABELS, PARTNER_SERVICES, type PartnerClient } from '../types';

const serviceLabel = (code: string) => PARTNER_SERVICES.find((s) => s.code === code)?.label ?? code;
const ENVIRONMENT = useMocks ? 'Demo (synthetic data)' : stagingBannerEnabled ? 'Staging' : 'Production';

/** The sections of a client workspace that exist today. Everything else is listed as not available, because a partner cannot yet open a client company. */
const BUILT_SECTIONS = new Set<string>(['Client Overview', 'Authorization History']);

export function PartnerClientPage() {
  const { relationshipId = '' } = useParams();
  const { membership, can } = usePartner();
  const orgId = membership.partnerOrganizationId;
  const client = useLoad((signal) => partnerGateway.getClient(orgId, relationshipId, signal), relationshipId, 'This client could not be loaded.');
  const assignments = useLoad((signal) => partnerGateway.listAssignments(orgId, relationshipId, signal), relationshipId, 'The assigned people could not be loaded.');
  const history = useLoad((signal) => partnerGateway.clientHistory(orgId, relationshipId, signal), relationshipId, 'The history could not be loaded.');
  const team = useLoad((signal) => (can('partner.assignments.manage') ? partnerGateway.listTeam(orgId, signal) : Promise.resolve([])), relationshipId, 'Your team could not be loaded.');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newLink, setNewLink] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  const [reason, setReason] = useState('');
  const [pick, setPick] = useState('');
  const session = useClientSession();
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);

  async function act(work: () => Promise<void>, failure: string) {
    setBusy(true);
    setError(null);
    try {
      await work();
      await Promise.all([client.reload(), assignments.reload(), history.reload()]);
    } catch (e) {
      setError(partnerErrorMessage(e, failure));
    } finally {
      setBusy(false);
    }
  }

  if (client.loading && !client.data) return <p className="pp-muted">Loading…</p>;
  if (!client.data) {
    return (
      <>
        <h1>Client</h1>
        <p role="alert" className="pp-error">{client.error ?? 'This client was not found.'}</p>
        <Link to="/partner/clients">Back to clients</Link>
      </>
    );
  }

  const c: PartnerClient = client.data;
  const name = c.clientCompanyName ?? c.clientName;
  const pending = c.status === 'InvitationSent' || c.status === 'AwaitingClientAcceptance';
  const finished = c.status === 'Revoked' || c.status === 'Ended';
  const services = c.grantedServices.length > 0 ? c.grantedServices : c.requestedServices;
  const assigned = new Set((assignments.data ?? []).map((a) => a.membershipId));
  const assignable = (team.data ?? []).filter((m) => m.isActive && !assigned.has(m.membershipId));

  return (
    <>
      <p><Link to="/partner/clients">← All clients</Link></p>

      <section className="pp-banner" aria-label="Client context">
        <h1 className="pp-banner-title">{name}</h1>
        <dl className="pp-facts">
          <div><dt>Status</dt><dd><RelationshipBadge status={c.status} /></dd></div>
          <div><dt>Your role</dt><dd>{PARTNER_ROLE_LABELS[membership.role]}</dd></div>
          <div><dt>{c.grantedServices.length > 0 ? 'Authorized services' : 'Services requested'}</dt><dd>{services.length === 0 ? 'None' : services.map(serviceLabel).join(', ')}</dd></div>
          <div><dt>Subscription plan</dt><dd>Not available yet</dd></div>
          <div><dt>Environment</dt><dd>{ENVIRONMENT}</dd></div>
          <div><dt>Access expires</dt><dd>{c.accessExpiresAtUtc ? new Date(c.accessExpiresAtUtc).toLocaleDateString() : 'No end date'}</dd></div>
        </dl>
      </section>

      {error && <p role="alert" className="pp-error">{error}</p>}

      {pending && (
        <section className="pp-card" aria-labelledby="pp-pending">
          <h2 id="pp-pending">Waiting for {c.invitedEmail}</h2>
          <p>
            The client's owner has not accepted yet.
            {c.invitationExpiresAtUtc ? ` The invitation expires ${new Date(c.invitationExpiresAtUtc).toLocaleDateString()}.` : ''}
          </p>
          {can('partner.clients.manage') && (
            <button
              type="button"
              className="pp-button"
              disabled={busy}
              onClick={() => void act(async () => setNewLink(invitationLink((await partnerGateway.resendInvitation(orgId, c.id)).invitationToken)), 'The invitation could not be re-sent.')}
            >
              Re-send the invitation (creates a new link)
            </button>
          )}
          {newLink && (
            <div className="pp-warn" role="note">
              <p><strong>This new link is shown once.</strong> The old link no longer works.</p>
              <input readOnly aria-label="New invitation link" value={newLink} onFocus={(e) => e.currentTarget.select()} />
            </div>
          )}
        </section>
      )}

      {(c.status === 'Active' || c.status === 'Onboarding') && can('partner.clients.read') && (
        <section className="pp-card" aria-labelledby="pp-open">
          <h2 id="pp-open">Work in this client</h2>
          <p className="pp-muted">
            Opening a client gives you a short session, renewed while you work, limited to what the client allowed and what your role permits. Everything you do there is recorded in your name.
          </p>
          {session.status === 'active' && session.delegation?.relationshipId === c.id ? (
            <Link className="pp-button pp-button--primary" to={`/partner/clients/${c.id}/workspace`}>Go to the workspace</Link>
          ) : (
            <button
              type="button"
              className="pp-button pp-button--primary"
              disabled={opening}
              onClick={async () => {
                setOpening(true);
                setOpenError(null);
                try {
                  await session.enter(orgId, c.id);
                  navigate(`/partner/clients/${c.id}/workspace`);
                } catch (e) {
                  setOpenError(partnerErrorMessage(e, 'You cannot open this client.'));
                } finally {
                  setOpening(false);
                }
              }}
            >
              {opening ? 'Opening…' : 'Open client workspace'}
            </button>
          )}
          {openError && <p role="alert" className="pp-error">{openError}</p>}
        </section>
      )}

      <section className="pp-card" aria-labelledby="pp-people">
        <h2 id="pp-people">People assigned to this client</h2>
        {assignments.error && <p role="alert" className="pp-error">{assignments.error}</p>}
        {(assignments.data ?? []).length === 0 ? (
          <p className="pp-muted">Nobody is assigned. A person can open a client only while assigned to it.</p>
        ) : (
          <ul className="pp-list">
            {(assignments.data ?? []).map((a) => (
              <li key={a.assignmentId}>
                {a.displayName ?? a.email} · {PARTNER_ROLE_LABELS[a.role]}
                {can('partner.assignments.manage') && !finished && (
                  <button type="button" className="pp-link-button" disabled={busy} onClick={() => void act(() => partnerGateway.unassign(orgId, c.id, a.assignmentId), 'The person could not be removed.')}>
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {can('partner.assignments.manage') && !finished && assignable.length > 0 && (
          <div className="pp-inline">
            <label>
              Assign someone
              <select value={pick} onChange={(e) => setPick(e.target.value)}>
                <option value="">Choose a person</option>
                {assignable.map((m) => <option key={m.membershipId} value={m.membershipId}>{m.displayName ?? m.email} ({PARTNER_ROLE_LABELS[m.role]})</option>)}
              </select>
            </label>
            <button type="button" className="pp-button" disabled={busy || !pick} onClick={() => void act(async () => { await partnerGateway.assign(orgId, c.id, pick); setPick(''); }, 'The person could not be assigned.')}>
              Assign
            </button>
          </div>
        )}
      </section>

      <section className="pp-card" aria-labelledby="pp-workspace">
        <h2 id="pp-workspace">Client workspace</h2>
        <Unavailable>You cannot open a client company from the portal yet, so these sections are not available. Overview and authorization history are below.</Unavailable>
        <ul className="pp-chips">
          {CLIENT_WORKSPACE_SECTIONS.map((section) => (
            <li key={section} className={BUILT_SECTIONS.has(section) ? 'pp-chip' : 'pp-chip pp-chip--off'} aria-disabled={BUILT_SECTIONS.has(section) ? undefined : true}>
              {section}{BUILT_SECTIONS.has(section) ? '' : ' (not available yet)'}
            </li>
          ))}
        </ul>
      </section>

      <section className="pp-card" aria-labelledby="pp-history">
        <h2 id="pp-history">Authorization history</h2>
        {history.error && <p role="alert" className="pp-error">{history.error}</p>}
        {(history.data ?? []).length === 0 ? (
          <p className="pp-muted">No history yet.</p>
        ) : (
          <ol className="pp-timeline">
            {(history.data ?? []).map((h, i) => (
              <li key={`${h.occurredAtUtc}-${i}`}>
                <time dateTime={h.occurredAtUtc}>{new Date(h.occurredAtUtc).toLocaleString()}</time> · {h.eventType.replace(/^Partner/, '').replace(/([a-z])([A-Z])/g, '$1 $2')}
                {h.actorEmail ? ` · ${h.actorEmail}` : ''}
                {h.reason ? ` · “${h.reason}”` : ''}
              </li>
            ))}
          </ol>
        )}
      </section>

      {can('partner.clients.manage') && !finished && (
        <section className="pp-card" aria-labelledby="pp-end">
          <h2 id="pp-end">End this relationship</h2>
          {!ending ? (
            <button type="button" className="pp-button pp-button--danger" onClick={() => setEnding(true)}>End relationship…</button>
          ) : (
            <div role="group" aria-label="Confirm ending the relationship">
              <p>Your firm will lose access to this client. Only a new invitation can restore it.</p>
              <label>
                Reason (at least 10 characters)
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
              </label>
              <div className="pp-actions">
                <button type="button" className="pp-button pp-button--danger" disabled={busy || reason.trim().length < 10} onClick={() => void act(async () => { await partnerGateway.endRelationship(orgId, c.id, reason.trim()); setEnding(false); setReason(''); }, 'The relationship could not be ended.')}>
                  End relationship
                </button>
                <button type="button" className="pp-button" onClick={() => setEnding(false)}>Cancel</button>
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
}
